import { htmlToText } from './html-to-text';

/**
 * What a message body *is*, now that a planner can put a photograph in one.
 *
 * Bodies were plain text everywhere: templates, queued inquiries, replies. A
 * rich body is sanitized HTML instead, and both shapes have to keep working
 * indefinitely — every template written before this existed is still `'text'`,
 * and rewriting them all on deploy would be a migration that can only lose
 * information (a body is *authored* text; re-encoding it as HTML and back is
 * not the identity function once a planner has typed an angle bracket).
 *
 * So the format travels with the body rather than being guessed from it.
 * Sniffing was the alternative and it is a trap: `<3` and `a < b` are prose
 * that a sniffer reads as markup, and the planner who typed them is the one
 * who pays for it.
 */
export const BODY_FORMATS = ['text', 'html'] as const;

export type BodyFormat = (typeof BODY_FORMATS)[number];

/**
 * Images inside a message body, as opposed to inside a signature.
 *
 * The ceiling is on what reaches the *server*, after the editor has already
 * re-encoded the drop (`frontend/src/lib/image-downscale.ts`). A phone photo is
 * 3-5MB and a look-book of six of them would be a message no vendor's mail
 * server accepts: Gmail's hard limit is 25MB on the assembled RFC 822 message,
 * and base64 inflates every byte by a third before it is counted. So the
 * editor aims for `MESSAGE_IMAGE_TARGET_BYTES` and this is the backstop for
 * anything that arrives another way.
 */
export const MESSAGE_ASSET_MAX_BYTES = 2 * 1024 * 1024;

/** Longest edge after re-encoding. Wider than any mail client renders inline. */
export const MESSAGE_IMAGE_MAX_EDGE = 1600;

/** What the editor re-encodes toward. Not a guarantee — a photograph of noise
 *  does not compress — which is why `MESSAGE_ASSET_MAX_BYTES` exists above. */
export const MESSAGE_IMAGE_TARGET_BYTES = 500 * 1024;

/**
 * Total image bytes in one body.
 *
 * Deliberately far below Gmail's 25MB: the cap has to hold for the *assembled*
 * message, which also carries the signature's images and a third again in
 * base64 overhead. It is also a kindness — a vendor reading on a phone over
 * hotel wifi is the person actually paying for a 20MB inquiry.
 */
export const MESSAGE_BODY_MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/** Images per body. A look-book, not an album; twelve is already generous for
 *  a first-contact inquiry, and the number exists so the failure is a clear
 *  refusal in the editor rather than a bounce a week later. */
export const MESSAGE_BODY_MAX_IMAGES = 12;

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ESCAPES[c]!);
}

/**
 * A body as readable plain text, whatever it was authored as.
 *
 * Every consumer that is not a mail client wants this: the `text/plain`
 * alternative part, the merge-variable check, the snippet, a test asserting on
 * what a vendor reads. `htmlToText` is the same renderer used for *inbound*
 * HTML mail, so an outbound body and an inbound one degrade identically.
 */
export function bodyToText(body: string, format: BodyFormat): string {
  if (format === 'text') return body;
  return htmlToText(body) ?? '';
}

/**
 * Seeds the rich editor from a body that was authored as plain text.
 *
 * Only ever runs on a legacy body being opened for the first time. The
 * conversion is intentionally boring — a blank line starts a paragraph, a
 * single newline is a `<br>` — because a planner who opens a template they
 * wrote last year must find it looking the way they left it, not reflowed.
 */
export function plainTextToHtml(text: string): string {
  const paragraphs = text.replace(/\r\n/g, '\n').split(/\n{2,}/);
  return paragraphs
    .map((para) => {
      const lines = para.split('\n').map(escapeHtml);
      // An empty trailing paragraph would render as a stray blank block in the
      // editor, and the planner cannot see why their cursor starts one line low.
      return lines.every((l) => l.trim() === '') ? '' : `<p>${lines.join('<br>')}</p>`;
    })
    .filter(Boolean)
    .join('');
}
